import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';

const GET_MUNCIPALITIES = gql`
  mutation getMunicipalities(
    $search: String
    $page: Int!
    $length: Int!
    $sortBy: String
    $filterBy: FiltersType
    $municipalityId: String
  ) {
    municipalities(
      search: $search
      page: $page
      length: $length
      sortBy: $sortBy
      filterBy: $filterBy
      municipalityId: $municipalityId
    ) {
      success
      message
      totalPages
      currentPage
      municipality {
        id
        name
        address
        logo
        phoneNumber
        inspectionDaysMin
        website
        email
        transferDocumentTitle
        instructionsAndNotes
        includeLegalDescription
        qualifyingExemptions {
          id
          municipalityId
          exemption
          status
          deleted
        }
        nomenclatureOfTheTax
        TaxCalculations {
          rate
          instructions
        }
        nomenclatureOfThePIN
        waterReading {
          required
          instructions
        }
        inspection {
          required
          instructions
          allowUsersToUploadDocs
        }
        proofofTaxPayment {
          required
          instructions
        }
        nomenclatureOfTheRecorderBox
        informationInTheRecordersBox
        additionalQuestions {
          id
          municipalityId
          question
          serialNo
          status
          deleted
        }
        status
      }
    }
  }
`;

export function useGetMuncipalitiesQuery() {
  return useMutation(GET_MUNCIPALITIES);
}

const GET_MUNICIPALITY = gql`
  query getMunicipality($id: String!) {
    municipality(id: $id) {
      success
      message
      municipality {
        id
        name
        address
        logo
        phoneNumber
        website
        email
        transferDocumentTitle
        instructionsAndNotes
        includeLegalDescription
        inspectionDaysMin
        stateId
        taxExemption
        stateInfo {
          label
          value
        }
        city
        counties {
          _id
          status
          municipalityId
          countyId
          label
          value
        }
        qualifyingExemptions {
          id
          municipalityId
          exemption
          status
          deleted
        }
        nomenclatureOfTheTax
        TaxCalculations {
          rate
          instructions
        }
        nomenclatureOfThePIN
        waterReading {
          required
          instructions
        }
        inspection {
          required
          instructions
          allowUsersToUploadDocs
        }
        proofofTaxPayment {
          required
          instructions
        }
        nomenclatureOfTheRecorderBox
        informationInTheRecordersBox
        additionalQuestions {
          id
          municipalityId
          question
          serialNo
          status
          deleted
        }
        status
      }
    }
  }
`;
export function useGetMunicipalityQuery(variables: { id: string }) {
  return useQuery(GET_MUNICIPALITY, {
    variables,
    fetchPolicy: 'no-cache',
  });
}

const CREATE_MUNICIPALITY = gql`
  mutation createMunicipality(
    $name: String!
    $address: String!
    $logo: String
    $phoneNumber: String!
    $website: String!
    $email: String!
    $transferDocumentTitle: String
    $instructionsAndNotes: String
    $includeLegalDescription: String
    $qualifyingExemptions: [MunicipalityExemptionsInput]
    $nomenclatureOfTheTax: String
    $TaxCalculationsRate: String
    $TaxCalculationsInstructions: String
    $nomenclatureOfThePIN: String
    $WaterReadingRequired: String
    $WaterReadingInstructions: String
    $InspectionRequired: String
    $InspectionInstructions: String
    $proofofTaxPaymentInstructions: String
    $proofofTaxPaymentRequired: String
    $InspectionAllowUsersToUploadDocs: String
    $nomenclatureOfTheRecorderBox: String
    $informationInTheRecordersBox: [String]
    $additionalQuestions: [MunicipalityQuestions]
    $status: String
    $inspectionDaysMin: Int
    $stateId: String!
    $city: String!
    $counties: [CountiesInput]
    $taxExemption: String
  ) {
    createMunicipality(
      name: $name
      address: $address
      logo: $logo
      phoneNumber: $phoneNumber
      website: $website
      email: $email
      transferDocumentTitle: $transferDocumentTitle
      instructionsAndNotes: $instructionsAndNotes
      includeLegalDescription: $includeLegalDescription
      qualifyingExemptions: $qualifyingExemptions
      nomenclatureOfTheTax: $nomenclatureOfTheTax
      TaxCalculationsRate: $TaxCalculationsRate
      TaxCalculationsInstructions: $TaxCalculationsInstructions
      nomenclatureOfThePIN: $nomenclatureOfThePIN
      WaterReadingRequired: $WaterReadingRequired
      WaterReadingInstructions: $WaterReadingInstructions
      InspectionRequired: $InspectionRequired
      InspectionInstructions: $InspectionInstructions
      InspectionAllowUsersToUploadDocs: $InspectionAllowUsersToUploadDocs
      nomenclatureOfTheRecorderBox: $nomenclatureOfTheRecorderBox
      informationInTheRecordersBox: $informationInTheRecordersBox
      additionalQuestions: $additionalQuestions
      status: $status
      proofofTaxPaymentRequired: $proofofTaxPaymentRequired
      proofofTaxPaymentInstructions: $proofofTaxPaymentInstructions
      inspectionDaysMin: $inspectionDaysMin
      stateId: $stateId
      city: $city
      counties: $counties
      taxExemption: $taxExemption
    ) {
      success
      message
      municipality {
        id
        name
        address
        logo
        phoneNumber
        website
        email
        transferDocumentTitle
        instructionsAndNotes
        includeLegalDescription
        inspectionDaysMin
        stateId
        taxExemption
        stateInfo {
          label
          value
        }
        city
        counties {
          _id
          status
          municipalityId
          countyId
          label
          value
        }
        qualifyingExemptions {
          id
          municipalityId
          exemption
          status
          deleted
        }
        nomenclatureOfTheTax
        TaxCalculations {
          rate
          instructions
        }
        nomenclatureOfThePIN
        waterReading {
          required
          instructions
        }
        proofofTaxPayment {
          required
          instructions
        }
        inspection {
          required
          instructions
          allowUsersToUploadDocs
        }
        nomenclatureOfTheRecorderBox
        informationInTheRecordersBox
        additionalQuestions {
          id
          municipalityId
          serialNo
          question
          status
          deleted
        }
        status
      }
    }
  }
`;
export function useCreateMunicipalityMutation() {
  return useMutation(CREATE_MUNICIPALITY);
}

const EDIT_MUNICIPALITY = gql`
  mutation editMunicipality(
    $id: String!
    $name: String!
    $address: String
    $logo: String
    $phoneNumber: String
    $website: String
    $email: String
    $transferDocumentTitle: String
    $instructionsAndNotes: String
    $includeLegalDescription: String
    $qualifyingExemptions: [MunicipalityExemptionsInput]
    $nomenclatureOfTheTax: String
    $TaxCalculationsRate: String
    $TaxCalculationsInstructions: String
    $nomenclatureOfThePIN: String
    $WaterReadingRequired: String
    $WaterReadingInstructions: String
    $proofofTaxPaymentRequired: String
    $proofofTaxPaymentInstructions: String
    $InspectionRequired: String
    $InspectionInstructions: String
    $InspectionAllowUsersToUploadDocs: String
    $nomenclatureOfTheRecorderBox: String
    $informationInTheRecordersBox: [String]
    $additionalQuestions: [MunicipalityQuestions]
    $status: String
    $inspectionDaysMin: Int
    $stateId: String!
    $city: String!
    $counties: [CountiesInput]
    $taxExemption: String
  ) {
    editMunicipality(
      id: $id
      name: $name
      address: $address
      logo: $logo
      phoneNumber: $phoneNumber
      website: $website
      email: $email
      transferDocumentTitle: $transferDocumentTitle
      instructionsAndNotes: $instructionsAndNotes
      includeLegalDescription: $includeLegalDescription
      qualifyingExemptions: $qualifyingExemptions
      nomenclatureOfTheTax: $nomenclatureOfTheTax
      TaxCalculationsRate: $TaxCalculationsRate
      TaxCalculationsInstructions: $TaxCalculationsInstructions
      nomenclatureOfThePIN: $nomenclatureOfThePIN
      WaterReadingRequired: $WaterReadingRequired
      WaterReadingInstructions: $WaterReadingInstructions
      InspectionRequired: $InspectionRequired
      InspectionInstructions: $InspectionInstructions
      InspectionAllowUsersToUploadDocs: $InspectionAllowUsersToUploadDocs
      nomenclatureOfTheRecorderBox: $nomenclatureOfTheRecorderBox
      informationInTheRecordersBox: $informationInTheRecordersBox
      additionalQuestions: $additionalQuestions
      proofofTaxPaymentRequired: $proofofTaxPaymentRequired
      proofofTaxPaymentInstructions: $proofofTaxPaymentInstructions
      status: $status
      inspectionDaysMin: $inspectionDaysMin
      stateId: $stateId
      city: $city
      counties: $counties
      taxExemption: $taxExemption
    ) {
      success
      message
      municipality {
        id
        name
        address
        logo
        phoneNumber
        website
        email
        transferDocumentTitle
        instructionsAndNotes
        includeLegalDescription
        stateId
        taxExemption
        stateInfo {
          label
          value
        }
        city
        counties {
          _id
          status
          municipalityId
          countyId
          label
          value
        }
        qualifyingExemptions {
          id
          municipalityId
          exemption
          status
          deleted
        }
        nomenclatureOfTheTax
        inspectionDaysMin
        TaxCalculations {
          rate
          instructions
        }
        nomenclatureOfThePIN
        waterReading {
          required
          instructions
        }
        inspection {
          required
          instructions
          allowUsersToUploadDocs
        }
        proofofTaxPayment {
          required
          instructions
        }
        nomenclatureOfTheRecorderBox
        informationInTheRecordersBox
        additionalQuestions {
          id
          municipalityId
          serialNo
          question
          status
          deleted
        }
        status
      }
    }
  }
`;
export function useEditMunicipalityMutation() {
  return useMutation(EDIT_MUNICIPALITY);
}

const GET_ACTIVE_COUNTIES = gql`
  mutation getAllActiveCountiesOfState($stateId: String!) {
    getAllActiveCountiesOfState(stateId: $stateId) {
      success
      message
      counties {
        _id
        name
        key
        status
      }
    }
  }
`;
export function useGetActiveCounties() {
  return useMutation(GET_ACTIVE_COUNTIES);
}
